import { Header, Message, Loader } from "semantic-ui-react";
import React, { useState, useEffect } from "react";
import { isString, startCase } from "lodash";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { CustomerType } from "../../customer/types";
import {
    validateCustomerPermitCode,
    loadCustomerBalance,
    validateCustomerDelinquency,
} from "../../customer/services/helpers";
import { DateFormat } from "utilities/dist/invoice/constants";
import { parseServerDateForClient } from "../../common/helpers";
import { PriceTierLabel } from "../../product/price-tier/components/label";
import { buildTabcId } from "utilities/dist/customer/helpers";
import { formatAmount } from "utilities/dist/helpers";
import { isFeatureActive, PaymentFeatureDetails } from "../../company/feature/helpers";

const InvoiceComponentCustomerHeader = ({ customer = {}, priceTier = null }) => {
    const { user } = useSelector(({ auth }) => ({
        user: auth.user,
    }));

    const [loading, setLoading] = useState(false);

    const [permitData, setPermitData] = useState(null);
    const [permitDataError, setPermitDataError] = useState("");
    const [delinquencyError, setDelinquencyError] = useState("");

    const [owing, setOwing] = useState(0);
    const [isEstablishmentError, setIsEstablishmentError] = useState(false);
    const hasPaymentFeature = user && isFeatureActive(user.company, PaymentFeatureDetails);

    useEffect(() => {
        if (customer && customer.id) {
            getCustomerPermitData();
            getCustomerDelinquency();

            if (hasPaymentFeature) getCustomerBalance();
        }
    }, [customer]);

    const getCustomerPermitData = () => {
        setLoading(true);
        validateCustomerPermitCode(customer.id)
            .then(response => {
                setPermitData(response);
                setLoading(false);
            })
            .catch(error => {
                const message = error?.response?.data?.message || error?.message;
                setPermitDataError(message);
                if (message.includes("Customer is not linked")) setIsEstablishmentError(true);
                setLoading(false);
            });
    };

    const getCustomerDelinquency = () => {
        setLoading(true);
        validateCustomerDelinquency(customer.id)
            .then(() => setLoading(false))
            .catch(error => {
                const message = error?.response?.data?.message || error?.message;
                setDelinquencyError(message);
                setLoading(false);
            });
    };

    const getCustomerBalance = () => {
        setLoading(true);
        loadCustomerBalance(customer.id)
            .then(({ owing }) => {
                setOwing(owing);
                setLoading(false);
            })
            .catch(error => {
                // eslint-disable-next-line no-console
                console.error("Error getting customer balance:", error);
                setLoading(false);
            });
    };

    const getFormatedDate = date => format(parseServerDateForClient(date), DateFormat);

    const isPermitExpiring = !!permitData && permitData?.expiringInDays < 30;
    const permitMessage = isPermitExpiring
        ? `TABC License: Will expire on ${getFormatedDate(permitData.expiration_date)}.`
        : `TABC License: Valid!`;

    return (
        <>
            <Header textAlign="right">
                <Header sub>
                    <b>TABC ID #{buildTabcId(customer)}</b>
                </Header>
                {startCase(customer.name)}
                <Header.Subheader>{startCase(customer.address)}</Header.Subheader>
                <Header.Subheader>
                    {[customer.city, customer.state, customer.zip].filter(isString).join(", ")}
                    {!!priceTier && (
                        <p className="tp-10 no-print">
                            <PriceTierLabel priceTier={priceTier} />
                        </p>
                    )}
                    {customer.contact_phone && (
                        <p>
                            <b>Phone:</b> {customer.contact_phone}
                        </p>
                    )}
                </Header.Subheader>
            </Header>
            {loading && <Loader active inline="centered" size="small" />}
            {!loading && (
                <>
                    {/* TABC Permit Status */}
                    <Message
                        className="no-print"
                        error={permitDataError}
                        warning={isPermitExpiring}
                        success={!permitDataError && !isPermitExpiring}
                        content={
                            !!permitDataError ? (
                                <>
                                    {`${permitDataError}`}
                                    {isEstablishmentError && (
                                        <p>
                                            <Link to={`/customer/${customer.id}/edit`}>
                                                {" "}
                                                Link it here.
                                            </Link>
                                        </p>
                                    )}
                                </>
                            ) : (
                                `${permitMessage}`
                            )
                        }
                        data-cy="customer_permit_status_message"
                    />
                    {/* Delinquency Status */}
                    <Message
                        className="no-print"
                        error={!!delinquencyError}
                        success={!delinquencyError}
                        content={
                            !!delinquencyError
                                ? `${delinquencyError}`
                                : `Delinquent List: No record found!`
                        }
                        data-cy="customer_delinquency_status_message"
                    />
                    {/* Customer Balance Status */}
                    {hasPaymentFeature && (
                        <Message
                            className="no-print"
                            warning={owing > 0}
                            success={owing === 0}
                            content={
                                owing > 0 ? (
                                    <>
                                        {customer.name} has {formatAmount(owing)} in unpaid
                                        invoices!{" "}
                                        <Link to={`/customer/${customer.id}/balance`}>
                                            View Details
                                        </Link>
                                    </>
                                ) : (
                                    `No unpaid invoices found!`
                                )
                            }
                            data-cy="customer_balance_status_message"
                        />
                    )}
                </>
            )}
        </>
    );
};

InvoiceComponentCustomerHeader.propTypes = {
    customer: CustomerType.isRequired,
};

export default InvoiceComponentCustomerHeader;
